import React, { useState } from 'react';
import { Tags, Loader2, Merge } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const SuggestionRow = ({ s, busy, onMerge }) => {
  const [keep, setKeep] = useState(s.a_count >= s.b_count ? s.a : s.b);
  const other = keep === s.a ? s.b : s.a;
  return (
    <div className="rounded-md border p-2.5 space-y-2" data-testid={`dup-suggestion-${s.a}`}>
      <p className="text-[11px] text-gray-500">Tick the correct spelling to keep:</p>
      {[[s.a, s.a_count], [s.b, s.b_count]].map(([n, c]) => (
        <label key={n} className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="radio" checked={keep === n} onChange={() => setKeep(n)} data-testid={`dup-keep-${n}`} />
          <span className="font-medium">{n}</span><span className="text-[11px] text-gray-400">used {c}×</span>
        </label>
      ))}
      <Button size="sm" disabled={busy} onClick={() => onMerge(keep, [other])} data-testid={`dup-merge-btn-${s.a}`}>
        <Merge className="h-3.5 w-3.5 mr-1" /> Merge into {keep}
      </Button>
    </div>
  );
};

export const PositionCleanupButton = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [keep, setKeep] = useState('');
  const [away, setAway] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/api/positions/duplicates').then(r => setData(r.data)).catch(() => setData({ suggestions: [], positions: [] }));
  const show = () => { setOpen(true); setData(null); load(); };

  const merge = async (k, m) => {
    if (!window.confirm(`Merge ${m.join(', ')} into ${k}? Every record using it will switch to ${k}. This can't be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post('/api/positions/merge', { keep: k, merge: m });
      toast({ title: `Merged into ${r.data.keep}`, description: `${r.data.records_updated} record(s) updated` });
      setKeep(''); setAway(''); load(); onDone?.();
    } catch (e) {
      toast({ title: 'Merge failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const opts = (data?.positions || []);
  return (
    <>
      <Button variant="outline" size="sm" onClick={show} className="gap-1.5" data-testid="position-cleanup-btn">
        <Tags className="h-3.5 w-3.5" /> Position cleanup
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="position-cleanup-dialog">
          <DialogHeader><DialogTitle>Position cleanup</DialogTitle></DialogHeader>
          <p className="text-xs text-gray-500">Merging switches every employee, onboarding card and offer letter to the kept name and removes the other from all position lists.</p>
          {!data ? <Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" /> : (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-semibold">Likely duplicates</p>
                {data.suggestions.length === 0 && <p className="text-sm text-gray-500" data-testid="no-dup-suggestions">No likely duplicates found.</p>}
                {data.suggestions.map(s => <SuggestionRow key={`${s.a}|${s.b}`} s={s} busy={busy} onMerge={merge} />)}
              </div>
              <div className="space-y-2 border-t pt-3">
                <p className="text-sm font-semibold">Merge any two positions</p>
                <Select value={away} onValueChange={setAway}>
                  <SelectTrigger data-testid="merge-away-select"><SelectValue placeholder="Position to remove…" /></SelectTrigger>
                  <SelectContent>{opts.filter(p => p.name !== keep).map(p => <SelectItem key={p.name} value={p.name}>{p.name} ({p.count})</SelectItem>)}</SelectContent>
                </Select>
                <Select value={keep} onValueChange={setKeep}>
                  <SelectTrigger data-testid="merge-keep-select"><SelectValue placeholder="…merge it into (keep)" /></SelectTrigger>
                  <SelectContent>{opts.filter(p => p.name !== away).map(p => <SelectItem key={p.name} value={p.name}>{p.name} ({p.count})</SelectItem>)}</SelectContent>
                </Select>
                <Button disabled={!keep || !away || busy} onClick={() => merge(keep, [away])} data-testid="manual-merge-btn">
                  <Merge className="h-4 w-4 mr-1" /> Merge
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
